import { useState } from 'react';
import { format } from 'date-fns';
import {
  ArrowSquareOutIcon,
  CopyIcon,
  DotsThreeVerticalIcon,
  LinkBreakIcon,
  LinkIcon,
} from '@phosphor-icons/react';
import { Card } from '@/components/ui/card';
import { CardTitle } from '@/components/ui/text';
import { IconButton } from '@/components/ui/icon-button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/cms/ui/dropdown-menu';
import LureoButton from '@/common/LureoButton';
import { Segmented } from '@/components/ui/segmented';
import type { StoryVersion } from '@/hooks/useStoryEditData';
import type { TemplateSubmission, TemplateUsageSummary } from '@/types/templateSubmissionTypes';
import { DEVICE_LABELS } from '@/components/publish-flow/deviceAvailability';
import type { PublishDevice } from '@/components/publish-flow/types';
import { VISIBILITY_HINT, VISIBILITY_OPTIONS } from '@/components/publish-flow/visibilityOptions';
import type { ChosenVisibility, StoryVisibility } from '@/infrastructure/api/v1/mappers/stories';
import {
  PILL_BASE,
  PILL_DOT,
  STORY_STATUS_PILL,
  SUBMISSION_PILL,
} from '@/components/cms/ui/pill-styles';

// A live link is revoked, never swapped
export interface DestinationShareLink {
  url: string;
  onCopy: () => void;
  onRevoke: () => void;
}

interface Props {
  versions: StoryVersion[];
  templateSubmission: TemplateSubmission | null;
  templateUsageSummary?: TemplateUsageSummary | null;
  templateUsageFailed?: boolean;
  onRetryTemplateStatus?: () => void;
  onUpdateSubmission: () => void;
  // Opens the template details panel. Absent when there is no template copy to show.
  onViewTemplate?: () => void;
  /** The story's permanent link while it is live (share links 3.6); null when it has none. */
  shareLink?: DestinationShareLink | null;
  /** Mints the permanent link when a live story has none (after a revoke). */
  onCreateShareLink?: () => void;
  onRetryShareLink?: () => void;
  visibility?: StoryVisibility;
  onVisibilityChange?: (v: ChosenVisibility) => void;
  visibilitySaving?: boolean;
  /** Starts a new draft from the latest version once every version is archived. */
  onEditCopy?: () => void;
}

// One row per destination this story has been published to. A story can be live
// for an audience and a template for other creators at the same time.
export function StoryDestinationsCard({
  versions,
  templateSubmission,
  templateUsageSummary,
  templateUsageFailed,
  onRetryTemplateStatus,
  onUpdateSubmission,
  onViewTemplate,
  shareLink,
  onCreateShareLink,
  onRetryShareLink,
  visibility,
  onVisibilityChange,
  visibilitySaving,
  onEditCopy,
}: Props) {
  const [showFeedback, setShowFeedback] = useState(false);

  const live = versions.find((v) => v.status === 'live');
  const pending = versions.find((v) => v.status === 'pending');
  const archived = versions.length > 0 && versions.every((v) => v.status === 'archived');
  const takenDown = visibility === 'takenDown';
  if (!live && !pending && !archived && !templateSubmission && !onRetryTemplateStatus) return null;

  return (
    <Card>
      <CardTitle className="mb-4">Where this story is published</CardTitle>
      <div className="space-y-3">
        {live && (
          <Row
            chip="Story · Live"
            chipClass={STORY_STATUS_PILL.live}
            meta={[
              live.publishedAt ? `Published ${format(new Date(live.publishedAt), 'd MMM')}` : 'Published',
              deviceList(live.devices),
            ]
              .filter(Boolean)
              .join(' · ')}
            action={shareLink && !takenDown ? <LinkMenu link={shareLink} /> : undefined}
          >
            {visibility && !takenDown && onVisibilityChange && (
              <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
                <p className="min-w-0 flex-[1_1_12rem] text-lureo-13 leading-relaxed text-lureo-ink-subtle">
                  {VISIBILITY_HINT[visibility]}
                </p>
                <Segmented
                  label="Who can find it"
                  options={VISIBILITY_OPTIONS}
                  value={visibility}
                  onChange={onVisibilityChange}
                  disabled={visibilitySaving}
                />
              </div>
            )}
            {takenDown ? (
              <p className="text-lureo-13 leading-relaxed text-lureo-ink-subtle">
                Lureo has taken this story down, so its links and PIN don't open it.
              </p>
            ) : shareLink ? (
              <>
                <Card padding="none" className="flex flex-wrap items-center gap-x-3 gap-y-2.5 py-2.5 pl-3 pr-2.5">
                  <span className="flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-full bg-lureo-aqua/45 text-lureo-emphasis-strong">
                    <LinkIcon className="h-3.5 w-3.5" />
                  </span>
                  <span className="min-w-0 flex-[1_1_12rem] select-text break-all font-lureo-font-mono text-lureo-14 text-lureo-ink leading-5">
                    {shareLink.url}
                  </span>
                  <LureoButton
                    variant="outline-2"
                    onClick={shareLink.onCopy}
                    className="h-7 shrink-0 gap-2 rounded-lureo-radius-button-pill bg-lureo-surface px-3 text-lureo-12 font-medium"
                  >
                    <CopyIcon className="h-3.5 w-3.5" />
                    <span>Copy link</span>
                  </LureoButton>
                </Card>
                <p className="text-lureo-13 leading-relaxed text-lureo-ink-subtle">
                  Every copy you share or print stays valid until you revoke it.
                </p>
              </>
            ) : onCreateShareLink ? (
              <div className="flex flex-wrap items-center gap-x-3 gap-y-2.5 rounded-lureo-radius border-lureo-border-width border-dashed border-lureo-line-strong py-2.5 pl-4 pr-2.5">
                <p className="min-w-0 flex-[1_1_12rem] text-lureo-13 text-lureo-ink-subtle">
                  This story has no public link right now. Create one to share it.
                </p>
                <LureoButton
                  variant="outline-2"
                  onClick={onCreateShareLink}
                  className="h-7 shrink-0 rounded-lureo-radius-button-pill bg-lureo-surface px-3 text-lureo-12 font-medium"
                >
                  <span>Create link</span>
                </LureoButton>
              </div>
            ) : onRetryShareLink ? (
              <div className="flex flex-wrap items-center gap-x-3 gap-y-2.5 rounded-lureo-radius border-lureo-border-width border-dashed border-lureo-line-strong py-2.5 pl-4 pr-2.5">
                <p className="min-w-0 flex-[1_1_12rem] text-lureo-13 text-lureo-ink-subtle">
                  We couldn't load this story's public link.
                </p>
                <LureoButton
                  variant="outline-2"
                  onClick={onRetryShareLink}
                  className="h-7 shrink-0 rounded-lureo-radius-button-pill bg-lureo-surface px-3 text-lureo-12 font-medium"
                >
                  <span>Try again</span>
                </LureoButton>
              </div>
            ) : null}
          </Row>
        )}

        {pending && (
          <Row
            chip="Story · Pending"
            chipClass={STORY_STATUS_PILL.pending}
            text={[
              platformSentence('Submitted for', pending.devices) ??
                'Submitted to your audience.',
              "We'll notify you within 7 days.",
              'Its preview link keeps working while it is in review.',
            ].join(' ')}
          />
        )}

        {archived && (
          <Row
            chip="Story · Archived"
            chipClass={STORY_STATUS_PILL.archived}
            text="This story is archived, so its links and PIN no longer open it. Edit a copy to work on it again and publish it with a new link."
            action={
              onEditCopy && (
                <LureoButton variant="outline-2" onClick={onEditCopy} className="h-8 rounded-full px-4">
                  Edit a copy
                </LureoButton>
              )
            }
          />
        )}

        {templateSubmission && (
          <Row
            chip={`Template · ${TEMPLATE_STATE_LABEL[templateSubmission.status]}`}
            chipClass={SUBMISSION_PILL[templateSubmission.status]}
            text={templateStateText(templateSubmission, templateUsageSummary, templateUsageFailed)}
            action={
              <span className="flex shrink-0 items-center gap-2">
                {templateSubmission.status === 'rejected' && templateSubmission.rejectionReason && (
                  <LureoButton
                    variant="no-outline"
                    onClick={() => setShowFeedback((v) => !v)}
                    className="h-8 rounded-lureo-radius-button-pill px-3"
                  >
                    {showFeedback ? 'Hide feedback' : 'View feedback'}
                  </LureoButton>
                )}
                {onViewTemplate && (
                  <LureoButton
                    variant="outline-2"
                    onClick={onViewTemplate}
                    className="h-8 rounded-lureo-radius-button-pill px-4"
                  >
                    View template
                  </LureoButton>
                )}
                {templateSubmission.status !== 'accepted' && (
                  <LureoButton
                    variant="outline-2"
                    onClick={onUpdateSubmission}
                    className="h-8 rounded-lureo-radius-button-pill px-4"
                  >
                    Update submission
                  </LureoButton>
                )}
              </span>
            }
          />
        )}

        {!templateSubmission && onRetryTemplateStatus && (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2.5 rounded-lureo-radius border-lureo-border-width border-dashed border-lureo-line-strong py-2.5 pl-4 pr-2.5">
            <p className="min-w-0 flex-[1_1_12rem] text-lureo-13 text-lureo-ink-subtle">
              We couldn't check whether this story is a template.
            </p>
            <LureoButton
              variant="outline-2"
              onClick={onRetryTemplateStatus}
              className="h-7 shrink-0 rounded-lureo-radius-button-pill bg-lureo-surface px-3 text-lureo-12 font-medium"
            >
              <span>Try again</span>
            </LureoButton>
          </div>
        )}

        {showFeedback && templateSubmission?.rejectionReason && (
          <div className="rounded-lureo-radius-lg border-lureo-border-width-medium border-lureo-coral-ink bg-lureo-coral/10 px-4 py-3 text-lureo-14 text-lureo-ink leading-5">
            <p className="font-medium text-lureo-brand-ink">Template feedback</p>
            <p className="mt-1 whitespace-pre-wrap">{templateSubmission.rejectionReason}</p>
          </div>
        )}
      </div>
    </Card>
  );
}

// "App, Web and Specs". Null when the version predates platform tracking
// (legacy payloads), so the row just omits the mention.
function deviceList(devices?: PublishDevice[] | null): string | null {
  if (!devices || devices.length === 0) return null;
  const labels = devices.map((d) => DEVICE_LABELS[d]);
  return labels.length === 1 ? labels[0] : `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`;
}

function platformSentence(prefix: string, devices?: PublishDevice[] | null): string | null {
  const list = deviceList(devices);
  return list ? `${prefix} ${list}.` : null;
}

function LinkMenu({ link }: { link: DestinationShareLink }) {
  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <IconButton label="Link options" tone="outline">
          <DotsThreeVerticalIcon className="h-4 w-4" />
        </IconButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        <DropdownMenuItem asChild>
          <a href={link.url} target="_blank" rel="noopener noreferrer">
            <ArrowSquareOutIcon className="h-4 w-4 text-lureo-on-menu-subtle" />
            Open link
          </a>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={link.onRevoke}>
          <LinkBreakIcon className="h-4 w-4 text-lureo-on-menu-subtle" />
          Revoke link
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

const TEMPLATE_STATE_LABEL: Record<TemplateSubmission['status'], string> = {
  draft: 'Draft',
  submitted: 'Submitted',
  accepted: 'Accepted',
  rejected: 'Not accepted',
};

function templateStateText(
  submission: TemplateSubmission,
  usage?: TemplateUsageSummary | null,
  usageFailed?: boolean,
): string {
  switch (submission.status) {
    case 'submitted':
      return submission.submittedAt
        ? `Submitted as a template on ${format(new Date(submission.submittedAt), 'd MMM')}. We'll notify you within 7 days.`
        : "Submitted as a template. We'll notify you within 7 days.";
    case 'accepted': {
      if (!usage && usageFailed) return "This template is live! We couldn't load how many people have used it right now.";
      const uses = usage?.qualifyingUseCount ?? 0;
      if (uses < 1) {
        return "This template is live! You'll earn when someone publishes their own version.";
      }
      return `This template is live! ${uses} ${
        uses === 1 ? 'person has' : 'people have'
      } published a qualifying version you could earn from.`;
    }
    case 'rejected':
      return 'Your template was not accepted. Address the feedback and submit it again.';
    case 'draft':
      return 'A draft submission is waiting to be sent.';
  }
}

function Row({
  chip,
  chipClass,
  meta,
  text,
  action,
  children,
}: {
  chip: string;
  chipClass: string;
  meta?: string;
  text?: string;
  action?: React.ReactNode;
  children?: React.ReactNode;
}) {
  return (
    <div className="rounded-lureo-radius border-lureo-border-width border-lureo-line bg-lureo-neutral-subtle/80 px-4 py-3.5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 flex-wrap items-center gap-x-2.5 gap-y-1">
          <span className={`${PILL_BASE} shrink-0 px-3 ${chipClass}`}>
            <span className={PILL_DOT} />
            {chip}
          </span>
          {meta && <span className="text-lureo-13 text-lureo-ink-subtle">{meta}</span>}
        </div>
        {action}
      </div>
      {text && <p className="mt-2.5 text-lureo-14 leading-relaxed text-lureo-ink">{text}</p>}
      {children && <div className="mt-2.5 space-y-2.5">{children}</div>}
    </div>
  );
}
